/**
 * COME SI PRESENTA UNA PARTITA a chi la gioca: una partita nata da una coda o da un sit&go non e' «un torneo»
 * (Luca, call del 05/10: «schermate di coda distinte da quelle di torneo, usando i tag»; r15).
 *
 * Per qTournaments una partita di coda e' un torneo da 2 gia' pieno e un sit&go un torneo da 3-32: e' cosi' che
 * nascono, ma non e' cosi' che li vive il giocatore. Il discriminante e' un DATO scritto alla creazione,
 * `origine` (`ORIGINI_TORNEO`), che il BFF porta anche sul dettaglio del torneo (`sanitizePublicTournament`,
 * `GET /v1/tournaments/:id`). I «tag» di cui parla Gionko sono questo campo: il giro delle code non scrive `tags`.
 *
 * ⛔ UNA regola sola: e' `famigliaDi` (`partiteInCorso.ts`, la stessa della Live), cioe' `origine` con il ripiego
 * sul prefisso dell'id (`coda-`, `sng-`) per i tornei nati prima del campo. Niente confronto sul NOME del torneo,
 * e niente seconda copia della regola: `torneoDiCoda` (`esiti.ts`) era una seconda copia, divergente, ed e' stata
 * tolta. Qui si decide solo cio' che la famiglia cambia nel TESTO.
 *
 * Fuori da qui restano come sono: `manuale` e `community` (sono «torneo»). La `sfida` fra amici e' un torneo da 2 (ADR-0163 §9)
 * ma per chi gioca e' una partita: ha la sua presentazione, che chi non la distingue tratta come «torneo» (com'era prima).
 */
import { famigliaDi, nomeCodaDi } from './partiteInCorso.ts';

export type Presentazione = 'coda' | 'sit-and-go' | 'torneo' | 'sfida';

/** Le presentazioni che hanno una scheda dove «tornare» (la Hub): la coda e il sit&go. */
export type PresentazioneDiCoda = Extract<Presentazione, 'coda' | 'sit-and-go'>;

/**
 * La presentazione di un torneo dato il suo id e, se c'e', la sua lettura. `torneo` e' tutto cio' che si ha: un
 * oggetto con `origine` (il dettaglio, una riga di lista), oppure niente — e allora decide il solo prefisso dell'id.
 */
export function presentazioneDi(tournamentId: string, torneo?: object | null): Presentazione {
  const origine = (torneo as { origine?: unknown } | null | undefined)?.origine;
  return famigliaDi({ tournamentId, origine: typeof origine === 'string' ? origine : null });
}

/**
 * Il titolo di un'azione della barra e del check-in modale: «<torneo> · Round <n>» per un torneo (e un sit&go, che ha
 * piu' turni), il NOME DELLA CODA senza «Round» per una partita di coda (un solo round: «Round 1» e' rumore, e il suffisso
 * con cui il giro battezza il torneo non e' un nome). `presentazione: null` = il torneo non e' stato ancora letto: il
 * titolo e' quello di sempre, senza indovinare dal prefisso.
 *
 * ⚠️ L'`origine` la porta la voce del motore (`GET /v1/me/tournaments/actions`) dal 06/10, con qlash-limulo#216 (a3f759c:
 * `sanitizeTournamentActions` la lascia passare per i cinque valori pubblici, altrimenti la chiave non c'e'; kb#368 p.3): la
 * barra e la modale di check-in passano `presentazioneDi` dalla voce stessa, senza la GET del dettaglio per riga. Il dettaglio
 * resta il RIPIEGO per la voce che l'`origine` non la porta (un BFF precedente alla #216, un valore non pubblico): non e' una
 * regressione, e' la lettura di prima.
 */
export function titoloAzioneOf(
  azione: { torneo: string; round: number | null },
  presentazione: Presentazione | null,
  conRound: (torneo: string, round: number) => string,
): string {
  if (presentazione === 'coda') return nomeCodaDi(azione.torneo);
  return azione.round === null ? azione.torneo : conRound(azione.torneo, azione.round);
}
